import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Divider,
  Paper,
  Stack,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { getUserByID, redeemGiftCard, User } from "../../utils";
import { Loading } from "../../components";
import { useAuth } from "../../utils/context";

export const BalanceSection: React.FC = () => {
  const { user: authUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    msg: string;
  } | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (authUser) {
        const fetchedUser = await getUserByID(authUser.userID);
        setUser(fetchedUser);
      }
    };
    fetchUserData();
  }, [authUser]);

  if (!user) return <Loading />;

  const onRedeem = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setAlert({ type: "error", msg: "Veuillez entrer un code." });
      return;
    }
    setSubmitting(true);
    setAlert(null);
    try {
      const res = await redeemGiftCard(trimmed);
      if (res.status === "success" && res.data !== null && authUser) {
        const updatedUser = await getUserByID(authUser.userID);
        setUser(updatedUser);
        setAlert({
          type: "success",
          msg: `Carte appliquée : +${res.data.creditedUSD.toFixed(2)} USD`,
        });
        setCode("");
      } else {
        setAlert({
          type: "error",
          msg: res.message || "Impossible d’utiliser ce code.",
        });
      }
    } catch (e: any) {
      setAlert({
        type: "error",
        msg:
          e?.response?.data?.message || "Erreur lors de l’utilisation du code.",
      });
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Box p={3}>
      <Typography variant="h5">Solde</Typography>
      <Divider sx={{ my: 2 }} />
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Solde Total
        </Typography>
        <Typography variant="h4" color="primary">
          {user.amount.toFixed(2)} BSB Coins
        </Typography>
        <Typography variant="body2">
          Montant total de tous les soldes estimé sur la base du taux de
          conversion le plus récent.
        </Typography>
      </Paper>
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Utiliser une carte BSB afin de créditer votre compte
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Code carte BSB"
            placeholder="BSB-XXXX-XXXX"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={onRedeem}
            disabled={submitting}
            sx={{ minWidth: 160 }}
          >
            {submitting ? "Traitement..." : "Utiliser"}
          </Button>
        </Stack>
        {alert && (
          <Box mt={2}>
            <Alert severity={alert.type} onClose={() => setAlert(null)}>
              {alert.msg}
            </Alert>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
